import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'

import { useStoredFlag } from '../app/shell/useStoredFlag'
import { CodexChatRail } from './CodexChatRail'
import { InsightChatWorkspace } from './InsightChatWorkspace'
import {
  isConversationId,
  newConversationId,
  onChatConversationSelected,
  saveChatConversation,
  savedConversationId,
  selectChatConversation,
} from './useCodexChat'
import styles from './ShellChat.module.scss'

function storedConversationId(): string {
  return savedConversationId() ?? newConversationId()
}

function relativeUrl(location: { pathname: string; search: string; hash: string }): string {
  return `${location.pathname}${location.search}${location.hash}`
}

function chatOrigin(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null
  const value = (state as { lumaReturnTo?: unknown }).lumaReturnTo
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null
  return value.split(/[?#]/, 1)[0] === '/chat' ? null : value
}

export function ShellChat({
  agentName = 'Luma',
  onPresentationChange,
}: {
  agentName?: string
  onPresentationChange?: (visible: boolean) => void
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const isChatRoute = location.pathname === '/chat'
  const isLumaRoute = location.pathname === '/insights'
  const chatParams = isChatRoute ? new URLSearchParams(location.search) : null
  const insightFingerprint = chatParams?.get('insight') || null
  const isInsightWorkspace = isChatRoute && insightFingerprint !== null
  const requestedId = chatParams?.get('c') ?? null
  const validRequestedId = isConversationId(requestedId) ? requestedId : null
  const requestsFreshConversation =
    isChatRoute && chatParams?.get('new') === '1'
  const prefillParam = isChatRoute ? new URLSearchParams(location.search).get('q') : null
  const [composerPrefill, setComposerPrefill] = useState('')
  useEffect(() => {
    if (prefillParam) setComposerPrefill(prefillParam)
  }, [prefillParam])
  const [conversationId, setConversationId] = useState(
    () => validRequestedId ?? storedConversationId(),
  )
  const handledFreshLocation = useRef<string | null>(null)
  const [collapsed, setCollapsed] = useStoredFlag('progetto_arca.chatRail.collapsed', !isChatRoute)
  const previousChatRouteForCollapse = useRef(isChatRoute)
  const chatStarted = useRef(false)
  const chatRunning = useRef(false)
  const ranWhileCollapsed = useRef(false)
  const skipNextLeavePulse = useRef(false)
  const [launcherPulse, setLauncherPulse] = useState(0)
  const [ready, setReady] = useState(false)
  const currentUrl = relativeUrl(location)
  const lastProductUrl = useRef(isChatRoute ? null : currentUrl)
  const originUrl = chatOrigin(location.state) ?? lastProductUrl.current
  const screenContextUrl = isChatRoute ? (originUrl ?? currentUrl) : currentUrl
  const displayCollapsed = !isChatRoute && (collapsed || previousChatRouteForCollapse.current)

  useEffect(() => {
    const foregroundChatVisible = (isChatRoute && !isInsightWorkspace)
      || (!isChatRoute && !isLumaRoute && !displayCollapsed)
    onPresentationChange?.(foregroundChatVisible)
  }, [displayCollapsed, isChatRoute, isInsightWorkspace, isLumaRoute, onPresentationChange])

  const collapseIntoBubble = useCallback(() => {
    setCollapsed(true)
    setLauncherPulse((current) => current + 1)
    if (chatRunning.current) ranWhileCollapsed.current = true
  }, [setCollapsed])

  useEffect(() => {
    if (launcherPulse === 0) return
    const timeout = window.setTimeout(() => setLauncherPulse(0), 720)
    return () => window.clearTimeout(timeout)
  }, [launcherPulse])

  useEffect(() => {
    return onChatConversationSelected((nextId, openWorkspace, newConversation) => {
      if (newConversation) chatStarted.current = false
      setReady(false)
      ranWhileCollapsed.current = false
      if (isChatRoute || openWorkspace) {
        const returnTo = isChatRoute ? originUrl : currentUrl
        navigate(`/chat?c=${encodeURIComponent(nextId)}`, {
          replace: isChatRoute,
          state: returnTo ? { lumaReturnTo: returnTo } : undefined,
        })
        return
      }
      setConversationId(nextId)
      setCollapsed(false)
    })
  }, [currentUrl, isChatRoute, navigate, originUrl, setCollapsed])

  useEffect(() => saveChatConversation(conversationId), [conversationId])

  useLayoutEffect(() => {
    const leftChatWorkspace = previousChatRouteForCollapse.current && !isChatRoute
    previousChatRouteForCollapse.current = isChatRoute
    if (!leftChatWorkspace) return
    if (skipNextLeavePulse.current) skipNextLeavePulse.current = false
    else collapseIntoBubble()
  }, [collapseIntoBubble, isChatRoute])

  useLayoutEffect(() => {
    if (!isLumaRoute) return
    setLauncherPulse(0)
    if (collapsed) return
    setCollapsed(true)
    if (chatRunning.current) ranWhileCollapsed.current = true
  }, [collapsed, isLumaRoute, setCollapsed])

  useEffect(() => {
    if (!isChatRoute) {
      lastProductUrl.current = currentUrl
      return
    }
    if (isInsightWorkspace) return
    if (requestsFreshConversation) {
      if (handledFreshLocation.current !== location.key) {
        handledFreshLocation.current = location.key
        const nextId = newConversationId()
        chatStarted.current = false
        setConversationId(nextId)
        navigate(`/chat?c=${encodeURIComponent(nextId)}`, {
          replace: true,
          state: location.state,
        })
      }
      return
    }
    if (validRequestedId && validRequestedId !== conversationId) {
      setConversationId(validRequestedId)
      return
    }
    if (requestedId !== conversationId) {
      navigate(`/chat?c=${encodeURIComponent(conversationId)}`, {
        replace: true,
        state: location.state,
      })
    }
  }, [
    conversationId,
    currentUrl,
    isChatRoute,
    isInsightWorkspace,
    location.key,
    location.state,
    navigate,
    requestedId,
    requestsFreshConversation,
    validRequestedId,
  ])

  const handleNewConversation = useCallback(() => {
    selectChatConversation(newConversationId(), { newConversation: true })
  }, [])
  const handleOpenWorkspace = useCallback(() => {
    navigate(`/chat?c=${encodeURIComponent(conversationId)}`, {
      state: { lumaReturnTo: currentUrl },
    })
  }, [conversationId, currentUrl, navigate])
  const handleMinimizeWorkspace = useCallback(() => {
    const minimize = () => {
      skipNextLeavePulse.current = true
      collapseIntoBubble()
      navigate(originUrl ?? (isInsightWorkspace ? '/insights?view=history' : '/'), { replace: true })
    }
    if (
      !chatStarted.current ||
      typeof document.startViewTransition !== 'function' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      minimize()
      return
    }
    document.startViewTransition(() => flushSync(minimize))
  }, [collapseIntoBubble, isInsightWorkspace, navigate, originUrl])
  const handleCollapsedChange = useCallback((next: boolean) => {
    setCollapsed(next)
    if (next) {
      if (chatRunning.current) ranWhileCollapsed.current = true
      return
    }
    setReady(false)
    ranWhileCollapsed.current = false
  }, [setCollapsed])
  const handleStartedChange = useCallback((started: boolean) => {
    chatStarted.current = started
  }, [])
  const handleRunningChange = useCallback((running: boolean) => {
    const wasRunning = chatRunning.current
    chatRunning.current = running
    if (running) {
      setReady(false)
      if (!isChatRoute && displayCollapsed) ranWhileCollapsed.current = true
      return
    }
    if (wasRunning && !isChatRoute && displayCollapsed && ranWhileCollapsed.current) {
      setReady(true)
    }
    ranWhileCollapsed.current = false
  }, [displayCollapsed, isChatRoute])

  return (
    <div
      data-agent-chat-scope="task-agent"
      data-chat-workspace={isChatRoute ? 'true' : 'false'}
      className={styles.layer}
    >
      {isInsightWorkspace ? (
        <InsightChatWorkspace
          fingerprint={insightFingerprint}
          agentName={agentName}
          onBack={handleMinimizeWorkspace}
          onNewConversation={handleNewConversation}
        />
      ) : (
        <CodexChatRail
          agentName={agentName}
          conversationId={conversationId}
          collapsed={isChatRoute ? false : displayCollapsed}
          workspace={isChatRoute}
          showEmptyLauncher={isChatRoute}
          composerPrefill={composerPrefill}
          screenContextUrl={screenContextUrl}
          onNewConversation={handleNewConversation}
          onOpenWorkspace={handleOpenWorkspace}
          onMinimizeWorkspace={handleMinimizeWorkspace}
          launcherPulse={launcherPulse}
          ready={ready}
          hidden={isLumaRoute}
          onCollapsedChange={handleCollapsedChange}
          onStartedChange={handleStartedChange}
          onRunningChange={handleRunningChange}
        />
      )}
    </div>
  )
}
